import { Vue, Component, Prop } from 'vue-property-decorator';

import ConfirmationModal from '../../ConfirmationModal/ConfirmationModal.vue';
import APIService from '../../../services/APIService';
import ValidationService from '../../../services/ValidationService';
import SemesterResponse from '../../../models/responses/SemesterResponse';
import SubjectDetailResponse from '../../../models/responses/SubjectDetailResponse';
import Validation from '../../../models/Validation';

/**
 * Semester subject form, to create and edit them.
 *
 * @param type - defines if this form is used for a semester or a subject
 */
@Component({
  name: 'SemesterSubjectForm',
  components: {
    ConfirmationModal,
  },
})
export default class SemesterSubjectForm extends Vue {
  @Prop(String) private readonly type: string | undefined;

  private name: string | null = null;
  private nameValidation: Validation = {
    type: 'name',
    isValid: false,
    message: null,
  };

  get selectedSemester(): SemesterResponse | null {
    return this.$store.state.selectedSemester;
  }

  get editSemester(): SemesterResponse | null {
    return this.$store.state.editSemester;
  }

  get editSubject(): SubjectDetailResponse | null {
    return this.$store.state.editSubject;
  }

  get validate(): (type: string, value: unknown) => Validation {
    return ValidationService.validate;
  }

  get title(): string | undefined {
    if (this.type === 'semester' && this.editSemester)
      return 'forms.semesterSubjectForm.semEditTitle';
    if (this.type === 'semester' && !this.editSemester)
      return 'forms.semesterSubjectForm.semTitle';
    if (this.type === 'subject' && this.editSubject)
      return 'forms.semesterSubjectForm.subjEditTitle';
    if (this.type === 'subject' && !this.editSubject)
      return 'forms.semesterSubjectForm.subjTitle';
  }

  /**
   * Checks if the form should be in edit mode and if yes, fill in the fields.
   */
  async mounted() {
    if (this.editSemester) {
      this.name = this.editSemester.name;
    } else if (this.editSubject) {
      this.name = this.editSubject.name;
    }
    this.nameValidation = this.validate('name', this.name);

    await setTimeout(() => {
      (this.$refs.nameInput as HTMLSdxInputElement).doFocus();
    }, 200);
  }

  /**
   * Updates the name property.
   *
   * @param value - new name value
   */
  private setName(value: string): void {
    this.name = value;
    this.nameValidation = this.validate('name', value);
  }

  /**
   * Toggles semester/subject form popup.
   */
  private closeSemesterSubjectForm(): void {
    this.$store.commit('toggleSemesterSubjectForm');
    this.$store.commit('resetEditForms');
  }

  /**
   * Saves a semester.
   */
  private async saveSemester(): Promise<void> {
    if (this.editSemester) {
      await APIService.updateSemester(this.editSemester.id, this.name);
      this.$emit('reload', this.editSemester.id);
    } else {
      await APIService.createSemester(this.name);
      this.$emit('reload');
    }
    this.closeSemesterSubjectForm();
  }

  /**
   * Saves a subject.
   */
  private async saveSubject(): Promise<void> {
    if (this.editSubject) {
      await APIService.updateSubject(this.editSubject.id, this.name);
      this.$emit('reload', this.editSubject.id);
    } else if (this.selectedSemester) {
      await APIService.createSubject(this.selectedSemester.id, this.name);
      this.$emit('reload', this.selectedSemester.id);
    }
    this.closeSemesterSubjectForm();
  }

  /**
   * Deletes a semester.
   */
  private async deleteSemester(): Promise<void> {
    if (this.selectedSemester) {
      await APIService.deleteSemester(this.selectedSemester.id);
    }
    this.$emit('reload');
    this.closeSemesterSubjectForm();
  }

  /**
   * Deletes a subject.
   */
  private async deleteSubject(): Promise<void> {
    if (this.editSubject) {
      await APIService.deleteSubject(this.editSubject.id);
      this.$router.push(`/dashboard?semester=${this.editSubject.semester.id}`);
    } else this.$router.push('/dashboard');
    this.closeSemesterSubjectForm();
  }

  /**
   * Checks if the form is valid and if so, calls the save function.
   *
   * @param type is the form of type semester or subject
   */
  private async submitWhenValid(type: string) {
    if (!this.nameValidation.isValid) return;
    if (type === 'semester') this.saveSemester();
    if (type === 'subject') this.saveSubject();
  }

  /**
   * Opens the confirmation modal for deleting a semester.
   */
  private openSemesterConfirmation() {
    this.$store.commit('openModal', {
      title: this.$t('forms.semesterSubjectForm.deleteSem'),
      text: this.$t('general.confirmation'),
      confirmationCallback: this.deleteSemester,
    });
  }

  /**
   * Opens the confirmation modal for deleting a subject.
   */
  private openSubjectConfirmation() {
    this.$store.commit('openModal', {
      title: this.$t('forms.semesterSubjectForm.deleteSubj'),
      text: this.$t('general.confirmation'),
      confirmationCallback: this.deleteSubject,
    });
  }
}
